import { useCallback, useEffect, useState } from "react";
import { submitCxGptSessionRating } from "util/copilotSessionApi";
import {
  RATING_IDLE_MS,
  ratingCardAfterQuestion,
  ratingHandled,
  ratingPromptReady,
  rememberRatingHandled,
} from "util/advisorSessionRating";

const NEAR_BOTTOM_PX = 80;

/**
 * End-of-chat rating card for GlobalCopilotPanel (rules in util/advisorSessionRating).
 * card is { sessionId, stars, comment, status, follow } while it belongs to the open
 * chat, otherwise null. onRated(sessionId, rating) runs after a rating is saved.
 */
const useAdvisorRatingCard = ({ enabled, session, loading, draft, listRef, onRated }) => {
  const [state, setState] = useState(null);
  const sessionId = session ? session.id : null;
  const ready = Boolean(enabled) && !state && ratingPromptReady({ session, loading, draft });
  const card = enabled && state && state.sessionId === sessionId ? state : null;
  const follow = Boolean(card && card.follow);
  const expanded = Boolean(card && card.stars > 0);

  // Ask once the chat goes quiet: a finished answer, no draft, nothing for RATING_IDLE_MS.
  useEffect(() => {
    if (!ready || ratingHandled(sessionId)) return undefined;
    const timer = setTimeout(() => {
      const list = listRef.current;
      setState({
        sessionId,
        stars: 0,
        comment: "",
        status: "ask",
        follow:
          !list || list.scrollHeight - list.scrollTop - list.clientHeight < NEAR_BOTTOM_PX,
      });
    }, RATING_IDLE_MS);
    return () => clearTimeout(timer);
  }, [ready, sessionId, listRef]);

  useEffect(() => {
    setState((prev) => (prev && prev.sessionId !== sessionId ? null : prev));
  }, [sessionId]);

  // Follow the card down only when the reader was at the bottom, or is filling it in.
  useEffect(() => {
    const list = listRef.current;
    if (list && (follow || expanded)) list.scrollTop = list.scrollHeight;
  }, [follow, expanded, listRef]);

  const patch = useCallback((id, next) => {
    setState((prev) => (prev && prev.sessionId === id ? { ...prev, ...next } : prev));
  }, []);

  const setStars = useCallback((stars) => {
    setState((prev) => (prev ? { ...prev, stars } : prev));
  }, []);

  const setComment = useCallback((comment) => {
    setState((prev) => (prev ? { ...prev, comment } : prev));
  }, []);

  const noteQuestion = useCallback(() => setState(ratingCardAfterQuestion), []);

  const clear = useCallback(() => setState(null), []);

  const skip = useCallback(() => {
    if (state) rememberRatingHandled(state.sessionId);
    setState(null);
  }, [state]);

  const submit = useCallback(async () => {
    if (!state || state.status === "sending" || !state.stars) return;
    const { sessionId: id, stars } = state;
    const comment = state.comment.trim();
    patch(id, { status: "sending" });
    const result = await submitCxGptSessionRating(id, {
      stars,
      ...(comment ? { comment } : {}),
    });
    if (!result || result.success !== true) {
      patch(id, { status: "error" });
      return;
    }
    rememberRatingHandled(id);
    if (onRated) onRated(id, result.rating || { stars, comment: comment || null });
    patch(id, { status: "thanks" });
  }, [onRated, patch, state]);

  return { card, setStars, setComment, submit, skip, noteQuestion, clear };
};

export default useAdvisorRatingCard;
